import { DcmJsDatasetWrapper } from '../dicom/dcmJsWrapper';
import { SubstitutionLog } from '../dicom/engine/charset';
import { WritePolicy } from '../dicom/engine/writer';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import {
    DicomStructureSet,
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { DicomRoi } from '../dicom/dicomRoi';
import { Modality } from '../dicom/modality';
import {
    DicomProcessor,
    dcmjsRemoveStructure,
} from '../services/dicomProcessor';
import { ChangeTracker } from '../changes/ChangeTracker';
import {
    StructureSetSnapshot,
    SyncStatus,
    TrackableObject,
} from '../changes/DomainChange';
import { newStructureSetId, RoiId, StructureSetId } from '../changes/ids';
import { ApprovedStructureError } from '../errors/DomainError';
import type { RecordedRoiReview } from '../provenance/RecordedRoiReview';
import type { RecordedDatasetOrigin } from '../provenance/RecordedDatasetOrigin';
import { Roi } from './roi';
import { Image3D } from './image3d';

/**
 * Patient-level structure set domain model that wraps DicomStructureSet.
 *
 * Provides a higher-level interface for structure set operations, combining
 * DICOM-level functionality with patient-specific concerns like image association.
 * Maintains immutability through the withX() pattern for all state changes.
 */
/** What a caller asks of a structure set when serialising it. */
export interface StructureSetSerializeRequest {
    /** Limit the object to these ROIs. Omit to keep all of them. */
    roiNumbers?: number[];
    /**
     * Declare this character set, for a receiving system that requires it.
     *
     * Omit it where nothing requires one. Asking is what makes reducing text
     * to fit the set right; without an ask the writer widens the object
     * rather than damaging text the user typed.
     */
    characterSet?: string;
    /** Collects any text the character set could not represent. */
    log?: SubstitutionLog;
    policy?: WritePolicy;
}

export class StructureSet implements Iterable<DicomRoi>, TrackableObject {
    readonly trackableKind = 'structureSet' as const;
    readonly id: StructureSetId;
    private _roiObjects: Roi[] = [];
    private tracker: ChangeTracker | null = null;
    private _syncStatus: SyncStatus = 'downloaded';
    private _recordedOrigin: RecordedDatasetOrigin | null = null;
    private _storedSopInstanceUid: string | undefined;

    public frameOfReferenceUid: string;

    public get structureSetLabel(): string {
        return this.dicomStructureSet.structureSetLabel;
    }

    public get structureSetDescription(): string {
        return this.dicomStructureSet.structureSetDescription;
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this.dicomStructureSet.approvalStatus;
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public get reviewerName(): string {
        return this.dicomStructureSet.reviewerName;
    }

    public get reviewTime(): string {
        return this.dicomStructureSet.reviewTime;
    }

    public get reviewDate(): string {
        return this.dicomStructureSet.reviewDate;
    }

    public get dicomUid(): string {
        return this.dicomStructureSet.sopInstanceUid;
    }

    /**
     * The SOP Instance UID the patient database stores for the set. A save of
     * edited content stores the file as a new SOP instance, so this moves away
     * from {@link dicomUid}, which stays the set's identity in the session.
     */
    public get storedSopInstanceUid(): string {
        return this._storedSopInstanceUid ?? this.dicomUid;
    }

    /** Records the SOP Instance UID the database answered a save with. */
    public adoptStoredSopInstanceUid(sopInstanceUid: string): void {
        this._storedSopInstanceUid = sopInstanceUid;
    }

    /** Whether the UID names this set, in the session or in the database. */
    public hasSopInstanceUid(uid: string): boolean {
        return uid === this.dicomUid || uid === this.storedSopInstanceUid;
    }

    public get seriesInstanceUid(): string {
        return this.dicomStructureSet.seriesInstanceUid;
    }

    public get manufacturer(): string {
        return this.dicomStructureSet.manufacturer;
    }

    public get manufacturerModelName(): string {
        return this.dicomStructureSet.manufacturerModelName;
    }

    public get softwareVersions(): string {
        return this.dicomStructureSet.softwareVersions;
    }

    get trackableId(): StructureSetId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
        for (const r of this._roiObjects) {
            r.attachTracker(tracker);
        }
    }

    /** New Roi[] collection — the domain-level wrappers. */
    get rois(): readonly Roi[] {
        return this._roiObjects;
    }

    /**
     * Rejects a mutation that would leave this set with two EXTERNAL ROIs
     * ({@link DuplicateExternalRoiError}). Single authority is the
     * DICOM-level guard — this is the domain wrapper's delegation so
     * services never reach into {@link dicomStructureSet} directly.
     * `excludeRoiNumber` lets a retype check ignore the ROI being retyped.
     */
    ensureExternalUnique(
        interpretedType: RoiInterpretedType | undefined,
        excludeRoiNumber?: number
    ): void {
        this.dicomStructureSet.ensureExternalUnique(
            interpretedType,
            excludeRoiNumber
        );
    }

    /**
     * Creates a new ROI in the structure set, wraps it in a domain
     * {@link Roi}, and appends it to the {@link rois} collection so the
     * Roi[] wrapper array stays consistent with the underlying
     * DicomStructureSet. The new Roi inherits this SS's tracker.
     *
     * Does NOT emit a changeset — creation tracking is the caller's
     * concern (services wrap this in a command() and markChanged).
     */
    createRoi(
        interpretedType?: RoiInterpretedType,
        structureName: string = ''
    ): Roi {
        const dicomRoi = this.dicomStructureSet.createRoi(
            interpretedType,
            structureName
        );
        const roi = new Roi(dicomRoi, this.dicomUid);
        if (this.tracker) {
            roi.attachTracker(this.tracker);
        }
        this._roiObjects = [...this._roiObjects, roi];
        return roi;
    }

    /**
     * Deletes the ROI with the given roiNumber from both the underlying
     * DicomStructureSet and the {@link rois} wrapper collection, keeping
     * the two consistent. No-op if no ROI with that number exists.
     *
     * Does NOT emit a changeset — deletion tracking is the caller's
     * concern.
     */
    deleteRoi(roiNumber: number): void {
        this.dicomStructureSet.deleteRoi(roiNumber);
        this._roiObjects = this._roiObjects.filter(
            (r) => r.roiNumber !== roiNumber
        );
    }

    /**
     * Internal/test seam: replaces the Roi[] wrapper collection wholesale.
     * Production code should rely on the constructor wrap plus
     * {@link syncRoiObjects} instead.
     */
    setRoiObjects(rois: Roi[]): void {
        this._roiObjects = rois;
    }

    /**
     * Reconciles the domain {@link rois} wrapper collection with the
     * underlying DicomStructureSet after dicom-level mutations (e.g.
     * structure-set copy, which clones/creates/deletes DicomRois without
     * going through {@link createRoi}/{@link deleteRoi}).
     *
     * Existing wrappers are kept (preserving RoiId identity), wrappers
     * whose DicomRoi no longer exists are dropped, and unwrapped
     * DicomRois get fresh wrappers that inherit this structure set's
     * tracker. Idempotent.
     */
    syncRoiObjects(): void {
        const wrapperByDicomRoi = new Map<DicomRoi, Roi>();
        for (const roi of this._roiObjects) {
            wrapperByDicomRoi.set(roi.dicomRoi, roi);
        }
        const synced: Roi[] = [];
        for (const dicomRoi of this.dicomStructureSet.rois.values()) {
            const existing = wrapperByDicomRoi.get(dicomRoi);
            if (existing) {
                synced.push(existing);
                continue;
            }
            const roi = new Roi(dicomRoi, this.dicomUid);
            if (this.tracker) {
                roi.attachTracker(this.tracker);
            }
            synced.push(roi);
        }
        this._roiObjects = synced;
    }

    get syncStatus(): SyncStatus {
        return this._syncStatus;
    }

    setSyncStatus(s: SyncStatus): void {
        this._syncStatus = s;
        this.tracker?.markChanged(this);
    }

    /**
     * How this set came to exist, as the connector recorded it; null when no
     * origin is recorded or none is known yet. Deliberately separate from
     * {@link generationModel}: that one marks output this session's AI
     * produced and gates capture of the "original AI" baseline, which a
     * reopened — possibly already edited — set must never qualify for.
     */
    get recordedOrigin(): RecordedDatasetOrigin | null {
        return this._recordedOrigin;
    }

    /**
     * Adopts the connector's record of this set's origin. Not an edit: the
     * record is load-time truth about the stored set, so no change is
     * reported to the tracker.
     */
    adoptRecordedOrigin(origin: RecordedDatasetOrigin | null): void {
        this._recordedOrigin = origin;
    }

    /**
     * Adopts the structures' own reviews the connector recorded: each
     * structure takes the one recorded for its ROI number, and a structure
     * with none recorded holds none.
     */
    adoptRecordedRoiReviews(reviews: readonly RecordedRoiReview[]): void {
        const byNumber = new Map(
            reviews.map((review) => [review.roiNumber, review])
        );
        for (const roi of this._roiObjects) {
            roi.adoptRecordedReview(byNumber.get(roi.roiNumber) ?? null);
        }
    }

    /** New domain method (preferred). Existing setter delegates to this. */
    rename(newName: string): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot rename approved structure set '${this.structureSetLabel}'`
            );
        }
        this.dicomStructureSet.structureSetLabel = newName;
        this.tracker?.markChanged(this);
    }

    setDescription(description: string): void {
        this._ensureCanModify();
        this.dicomStructureSet.structureSetDescription = description;
        this.tracker?.markChanged(this);
    }

    getRoiById(id: StructureSetId | string): Roi | undefined {
        return this._roiObjects.find((r) => r.id === id);
    }

    /**
     * The id of this set's ROI numbered `roiNumber`, or undefined when the
     * set has none. An ROI Number is unique only within its set; the id is
     * unique across sets.
     */
    roiIdOf(roiNumber: number): RoiId | undefined {
        return this._roiObjects.find((r) => r.roiNumber === roiNumber)?.id;
    }

    captureSnapshot(): StructureSetSnapshot {
        return {
            id: this.id,
            displayName: this.structureSetLabel,
            description: this.structureSetDescription,
            approvalStatus: this.approvalStatus,
            reviewerName: this.reviewerName,
            frameOfReferenceUid: this.frameOfReferenceUid,
            sopInstanceUid: this.dicomStructureSet.sopInstanceUid,
            seriesInstanceUid: this.seriesInstanceUid,
            imageSeriesUid: this._image?.seriesUid ?? null,
            roiCount: this._roiObjects.length,
            syncStatus: this._syncStatus,
        };
    }

    /**
     * Map where key is the {@link DicomRoi.roiNumber} and value is the {@link DicomRoi}.
     * @deprecated Prefer the {@link rois} array of domain-level {@link Roi} wrappers.
     */
    public get dicomRois(): ReadonlyMap<number, DicomRoi> {
        return this.dicomStructureSet.rois;
    }

    public get roiArray(): DicomRoi[] {
        return Array.from(this.dicomStructureSet.rois.values());
    }

    public get image(): Image3D | null {
        return this._image;
    }

    public set image(image: Image3D | null) {
        this._image = image;
    }

    public get numberOfStructures(): number {
        return this.dicomStructureSet.numberOfStructures;
    }

    /**
     * Get an ROI by its roiNumber.
     * @param roiNumber - The number of the ROI you want to retrieve.
     * @returns The corresponding ROI, or undefined if not found.
     */
    public getRoiByNumber(roiNumber: number): DicomRoi | undefined {
        return this.dicomStructureSet.rois.get(roiNumber);
    }

    /**
     * Sets approval status with cascading (mutable version).
     * @param approvalStatus The new approval status
     * @param reviewerName The reviewer name
     * @param cascade If true, also updates all ROI approval statuses to match
     */
    public setApprovalStatus(
        approvalStatus: DICOMApprovalStatus,
        reviewerName: string,
        cascade: boolean = true
    ): void {
        this.dicomStructureSet.setApprovalStatus(approvalStatus, reviewerName);
        if (cascade) {
            // Cascade through the tracked Roi wrappers (not the raw DICOM
            // objects) so each per-ROI approval flip is marked and reaches
            // the coalesced changeset. Writing roi.approvalStatus directly
            // bypassed the tracker, so consumers saw only the SS entry and
            // none of the ROIs.
            for (const roi of this._roiObjects) {
                switch (approvalStatus) {
                    case DICOMApprovalStatus.APPROVED:
                        roi.approve(reviewerName);
                        break;
                    case DICOMApprovalStatus.REJECTED:
                        roi.reject(reviewerName);
                        break;
                    default:
                        roi.unapprove();
                        break;
                }
            }
        }
        // Approval transitions ARE persistence-relevant: persistence and
        // the bridge both need to see the new status / reviewer.
        this.tracker?.markChanged(this);
    }

    /**
     * Get all ROIs with a specific approval status.
     * @param approvalStatus - The approval status to filter by.
     * @returns An array of ROIs with the specified approval status.
     *
     */
    public getRoiByApprovalStatus(
        approvalStatus: DICOMApprovalStatus
    ): DicomRoi[] {
        return this.roiArray.filter(
            (roi) => roi.approvalStatus === approvalStatus
        );
    }

    /**
     * Determines if the structure set approval status should cascade based on ROI states.
     *
     * @returns The cascaded approval status, or null if no change needed:
     * - APPROVED if all ROIs are approved
     * - REJECTED if all ROIs are rejected
     * - UNAPPROVED if ROIs have mixed states
     * - null if current status already matches the calculated status
     */
    public getCascadingApprovalStatus(): DICOMApprovalStatus | null {
        if (this.numberOfStructures === 0) {
            return null;
        }

        const approvedCount = this.getRoiByApprovalStatus(
            DICOMApprovalStatus.APPROVED
        ).length;
        const rejectedCount = this.getRoiByApprovalStatus(
            DICOMApprovalStatus.REJECTED
        ).length;

        let targetStatus: DICOMApprovalStatus;

        if (approvedCount === this.numberOfStructures) {
            targetStatus = DICOMApprovalStatus.APPROVED;
        } else if (rejectedCount === this.numberOfStructures) {
            targetStatus = DICOMApprovalStatus.REJECTED;
        } else {
            targetStatus = DICOMApprovalStatus.UNAPPROVED;
        }

        return this.approvalStatus === targetStatus ? null : targetStatus;
    }

    /**
     * A copy of the underlying DICOM dataset, fully detached from the live
     * structure set, so callers may mutate it (rename ROIs, reassign UIDs, …)
     * before serialising without affecting this object.
     */
    private cloneDataset(): DcmJsDatasetWrapper {
        return new DcmJsDatasetWrapper(
            DicomProcessor.cloneDicomDataset(this.dicomStructureSet)
        );
    }

    /** A copy holding only the ROIs in `roiNumbers`. */
    private cloneKeepingOnlyRois(roiNumbers: number[]): DcmJsDatasetWrapper {
        const dicomClone = this.cloneDataset();
        const keptRoiNumbers = new Set(roiNumbers);
        for (const roiNumber of this.dicomStructureSet.rois.keys()) {
            if (!keptRoiNumbers.has(roiNumber)) {
                dcmjsRemoveStructure(dicomClone, roiNumber);
            }
        }
        return dicomClone;
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomStructureSet.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomStructureSet.isNew;
    }

    public get isExported(): boolean {
        return this.dicomStructureSet.isExported;
    }

    /**
     * Records that this structure set was exported/uploaded. Tracked —
     * the isNew/isExported flags feed version-cached read models (export
     * dialog), which must invalidate on this transition.
     */
    public setExported(): void {
        // Mark before mutating — see DoseMatrix.setExported.
        this.tracker?.markChanged(this);
        this.dicomStructureSet.setExported();
    }

    /**
     * Serialises the structure set.
     *
     * The live object is written where nothing is asked of it, which is what
     * keeps every unedited attribute's original bytes. Restricting the ROIs
     * and declaring a character set are both edits, so either is made on a
     * copy: the structure set the user still has open must not change because
     * it was exported.
     */
    public export(request: StructureSetSerializeRequest = {}): Blob {
        const { roiNumbers, characterSet, log, policy = {} } = request;
        if (!roiNumbers && !characterSet) {
            return this.dicomStructureSet.datasetWrapper.asBlob(log, policy);
        }

        const dicomClone = roiNumbers
            ? this.cloneKeepingOnlyRois(roiNumbers)
            : this.cloneDataset();
        if (!characterSet) {
            return dicomClone.asBlob(log, policy);
        }
        // Asking for a set is what makes reducing text to fit it acceptable:
        // the export is for a system that requires it. Without the ask, the
        // writer widens the declaration instead of damaging the text.
        dicomClone.setCharacterSet(characterSet);
        return dicomClone.asBlob(log, { ...policy, characterSetChosen: true });
    }
    /**
     * Exports the structure set as a Monaco DIR file.
     * This is a custom format used for sharing structure sets in a specific way.
     * @param patientName - The name of the patient, used in the export.
     * @returns A Blob representing the exported structure set in Monaco format.
     */
    public exportAsMonacoFile(patientName: string): Blob {
        if (!this.image) {
            throw new Error(
                'Cannot export Monaco file without an associated image.'
            );
        }

        const modality = this.image.seriesMetadata.modality;

        if (modality !== Modality.CT && modality !== Modality.MR) {
            throw new Error(
                'Can only export Monaco file for CT or MR modalities.'
            );
        }
        const formatOrientation = (num: number): string => {
            if (num === 0) {
                return '0';
            }
            if (num === 1) {
                return '1';
            }
            if (num === -1) {
                return '-1';
            }
            return num.toFixed(6);
        };

        const imagePositionLines = Array.from(
            this.image.firstSlice.imagePosition
        ).map((x) => x.toFixed(4));
        // export only the X and Y direction cosines as defined in DICOM
        const imageOrientationLines = Array.from(
            this.image.firstSlice.imageOrientation.slice(0, 6) // Only take the first 6 elements for orientation
        ).map(formatOrientation);

        const monacoDirFileLines: (string | number)[] = [
            '!Version=2',
            modality,
            this.image.sopClassUid,
            this.image.seriesMetadata.seriesInstanceUID,
            this.image.firstSlice.studyInstanceUID,
            'NO_ID',
            patientName,
            this.image.frameOfReferenceUid,
            ...imagePositionLines,
            ...imageOrientationLines,
            'RTSTRUCT',
            '1.2.840.10008.5.1.4.1.1.481.3', // DICOMClassSopUIDs.rt_struct.value
            this.dicomStructureSet.sopInstanceUid,
            this.dicomStructureSet.seriesInstanceUid,
            'NO_ID',
            this.structureSetDescription || 'NO_DESCRIPTION',
            '',
            '',
            '',
            '',
            0,
            0,
            0,
            0,
            0,
            0,
        ];

        const monacoDirFileContent = monacoDirFileLines.join('\n') + '\n';

        return new Blob([monacoDirFileContent], {
            type: 'text/plain;charset=utf-8',
        });
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(
                `Cannot modify approved structure set '${this.structureSetLabel}'`
            );
        }
    }

    constructor(
        public readonly dicomStructureSet: DicomStructureSet,
        frameOfReferenceUid: string,
        private _image: Image3D | null,
        id: StructureSetId = newStructureSetId()
    ) {
        this.frameOfReferenceUid = frameOfReferenceUid;
        this.id = id;
        // Wrap any DicomRois present at construction so the domain rois
        // collection is a constructor-owned invariant rather than an
        // external hydration step. Structure sets created mid-session
        // (copy, import) are therefore consistent from birth; a later
        // attachTracker propagates the tracker to these wrappers.
        this.syncRoiObjects();
        this.markModelGeneratedRois();
    }

    /**
     * For a set produced by this session's AI, marks the structures the model
     * produced: those the result marks AUTOMATIC. Only a result the connector
     * attributes to its task records them, so only that one is marked. A result
     * of a task given an input structure set is not: it can carry that set's
     * structures through, AUTOMATIC ones included, and they cannot be told
     * apart from the model's own. Runs once, at construction: a structure added
     * to the set later is never the model's.
     */
    private markModelGeneratedRois(): void {
        const model = this.dicomStructureSet.generationModel;
        if (!model?.provenanceTaskId || model.resultCarriesInputStructures) {
            return;
        }
        for (const roi of this._roiObjects) {
            if (
                roi.dicomRoi.roiGenerationAlgorithm ===
                RoiGenerationAlgorithmType.AUTOMATIC
            ) {
                roi.markModelGenerated();
            }
        }
    }

    public [Symbol.iterator](): Iterator<DicomRoi> {
        return this.dicomStructureSet[Symbol.iterator]();
    }
}
